/**
 * Referral Section Component - v2.8
 * Displays user's referral info in their dashboard
 * Only shown for users who can invite (came via referral)
 */

import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Copy, Check, Users, DollarSign, Network, Wallet, Save, AlertCircle, Clock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useWallet } from '@solana/wallet-adapter-react'
import { toast } from 'sonner'
import { api } from '../../services/api'
import { useClaimReward } from '../../hooks/useClaimReward'
import TaxPreviewModal from '../TaxPreviewModal'

interface ReferralInfo {
  referralCode: string
  referralLink: string
  invitedUsersCount: number
  totalInvestmentsUSDT: number
  level: number
}

interface InvitedUser {
  id: string
  username: string | null
  walletAddress: string | null
  level: number | null
  joinedAt: string
  totalInvestedUSDT: number
  totalInvestedTAKARA: number
}

interface ReferralStats {
  byLevel: {
    L1: {
      users: InvitedUser[]
      userCount: number
    }
    L2: {
      users: InvitedUser[]
      userCount: number
    }
    L3: {
      users: InvitedUser[]
      userCount: number
    }
  }
}

// Solana address validation regex
const SOLANA_ADDRESS_REGEX = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/

/** Format remaining seconds as MM:SS */
function formatCountdown(expiresAt: string): string | null {
  const remaining = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000))
  if (remaining <= 0) return null
  const m = Math.floor(remaining / 60)
  const s = remaining % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

/** Extracted referral rewards claim block with lock support */
function ReferralRewardsClaim({ user, publicKey, claimReward, queryClient,
  isUsdtReferralModalOpen, setIsUsdtReferralModalOpen,
  isTakaraReferralModalOpen, setIsTakaraReferralModalOpen,
}: {
  user: any
  publicKey: any
  claimReward: ReturnType<typeof useClaimReward>
  queryClient: any
  isUsdtReferralModalOpen: boolean
  setIsUsdtReferralModalOpen: (v: boolean) => void
  isTakaraReferralModalOpen: boolean
  setIsTakaraReferralModalOpen: (v: boolean) => void
}) {
  const [, setTick] = useState(0)
  const hasLock = user?.claimLockedUntil?.REFERRAL_USDT || user?.claimLockedUntil?.REFERRAL_TAKARA
  useEffect(() => {
    if (!hasLock) return
    const id = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(id)
  }, [hasLock])

  const usdtLockTime = user?.claimLockedUntil?.REFERRAL_USDT ? formatCountdown(user.claimLockedUntil.REFERRAL_USDT) : null
  const takaraLockTime = user?.claimLockedUntil?.REFERRAL_TAKARA ? formatCountdown(user.claimLockedUntil.REFERRAL_TAKARA) : null

  const handleUsdtClick = () => {
    if (!publicKey) {
      toast.error('Please connect your wallet first')
      return
    }
    setIsUsdtReferralModalOpen(true)
  }

  const handleTakaraClick = () => {
    if (!publicKey) {
      toast.error('Please connect your wallet first')
      return
    }
    setIsTakaraReferralModalOpen(true)
  }

  return (
    <div className="mt-4 pt-4 border-t border-green-900/20">
      <div className="flex items-center gap-2 mb-3">
        <DollarSign className="h-4 w-4 text-green-400" />
        <h4 className="text-sm font-medium text-white">Referral Rewards</h4>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {/* Referral USDT — available or locked */}
        {((user?.availableReferralUSDT ?? 0) > 0 || usdtLockTime) && (
          <div className="bg-background-elevated rounded-lg p-3 flex flex-col">
            <div className="text-xs text-gray-400 mb-1">Available USDT</div>
            {usdtLockTime ? (
              <>
                <div className="flex items-center gap-1.5 text-yellow-400 mb-2">
                  <Clock className="h-3.5 w-3.5" />
                  <span className="text-xs font-medium">Claim in progress</span>
                </div>
                <div className="text-xs text-gray-400 mb-2">Expires in {usdtLockTime}</div>
                <button disabled className="btn-gold w-full py-1.5 rounded-lg font-medium text-xs opacity-50 cursor-not-allowed mt-auto">
                  Locked
                </button>
              </>
            ) : (
              <>
                <div className="text-lg font-bold text-gold-500 mb-2">
                  ${(user!.availableReferralUSDT!).toFixed(2)}
                </div>
                <button
                  onClick={handleUsdtClick}
                  disabled={claimReward.loading}
                  className="btn-gold w-full py-1.5 rounded-lg font-medium text-xs disabled:opacity-50 disabled:cursor-not-allowed mt-auto"
                >
                  {claimReward.loading ? 'Claiming...' : 'Claim USDT'}
                </button>
              </>
            )}
          </div>
        )}
        {/* Referral TAKARA — available or locked */}
        {((user?.availableReferralTAKARA ?? 0) > 0 || takaraLockTime) && (
          <div className="bg-background-elevated rounded-lg p-3 flex flex-col">
            <div className="text-xs text-gray-400 mb-1">Available TAKARA</div>
            {takaraLockTime ? (
              <>
                <div className="flex items-center gap-1.5 text-yellow-400 mb-2">
                  <Clock className="h-3.5 w-3.5" />
                  <span className="text-xs font-medium">Claim in progress</span>
                </div>
                <div className="text-xs text-gray-400 mb-2">Expires in {takaraLockTime}</div>
                <button disabled className="bg-green-600 opacity-50 cursor-not-allowed text-white w-full py-1.5 rounded-lg font-medium text-xs mt-auto">
                  Locked
                </button>
              </>
            ) : (
              <>
                <div className="text-lg font-bold text-green-400 mb-2">
                  {(user!.availableReferralTAKARA!).toFixed(2)}
                </div>
                <button
                  onClick={handleTakaraClick}
                  disabled={claimReward.loading}
                  className="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white w-full py-1.5 rounded-lg font-medium text-xs transition-colors mt-auto"
                >
                  {claimReward.loading ? 'Claiming...' : 'Claim TAKARA'}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <TaxPreviewModal
        isOpen={isUsdtReferralModalOpen}
        onClose={() => setIsUsdtReferralModalOpen(false)}
        onConfirm={() => {
          claimReward.claimReferralUsdt()
            .then(() => {
              toast.success('Referral USDT claimed!')
              queryClient.invalidateQueries({ queryKey: ['currentUser'] })
            })
            .catch((err: any) => {
              toast.error(err.message || 'Failed to claim referral USDT')
              // Refetch to pick up claimLockedUntil from backend
              queryClient.invalidateQueries({ queryKey: ['currentUser'] })
            })
            .finally(() => {
              setIsUsdtReferralModalOpen(false)
            })
        }}
        amount={user?.availableReferralUSDT ?? 0}
        tokenSymbol="USDT"
        isPending={claimReward.loading}
      />

      <TaxPreviewModal
        isOpen={isTakaraReferralModalOpen}
        onClose={() => setIsTakaraReferralModalOpen(false)}
        onConfirm={() => {
          claimReward.claimReferralTakara()
            .then(() => {
              toast.success('Referral TAKARA claimed!')
              queryClient.invalidateQueries({ queryKey: ['currentUser'] })
            })
            .catch((err: any) => {
              toast.error(err.message || 'Failed to claim referral TAKARA')
              queryClient.invalidateQueries({ queryKey: ['currentUser'] })
            })
            .finally(() => {
              setIsTakaraReferralModalOpen(false)
            })
        }}
        amount={user?.availableReferralTAKARA ?? 0}
        tokenSymbol="TAKARA"
        isPending={claimReward.loading}
      />
    </div>
  )
}

export default function ReferralSection() {
  const { t } = useTranslation(['referral', 'common'])
  const [copiedCode, setCopiedCode] = useState(false)
  const [walletInput, setWalletInput] = useState('')
  const [walletError, setWalletError] = useState('')
  const queryClient = useQueryClient()

  const { publicKey } = useWallet()
  const claimReward = useClaimReward()
  const [isTakaraReferralModalOpen, setIsTakaraReferralModalOpen] = useState(false)
  const [isUsdtReferralModalOpen, setIsUsdtReferralModalOpen] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['user-referral-info'],
    queryFn: async () => {
      const response = await api.getUserReferralInfo()
      return response.data as ReferralInfo | null
    },
    retry: false,
  })

  // Fetch current user to get referralWalletAddress
  const { data: userResponse } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => api.getCurrentUser(),
  })

  // Fetch referral stats with invited users list
  const { data: statsResponse } = useQuery({
    queryKey: ['user-referral-stats'],
    queryFn: async () => {
      const response = await api.getUserReferralStats()
      return response.data as ReferralStats | null
    },
    retry: false,
    enabled: !!data?.referralCode, // Only fetch if user can invite
  })

  const user = userResponse?.data

  // Mutation to set referral wallet address
  const setWalletMutation = useMutation({
    mutationFn: (walletAddress: string) => api.setReferralWalletAddress(walletAddress),
    onSuccess: () => {
      toast.success(t('referral:section.walletSet'))
      setWalletInput('')
      setWalletError('')
      // Invalidate user query to refresh the data
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || t('referral:section.walletSetError')
      toast.error(message)
      setWalletError(message)
    },
  })

  // Don't show section if user can't invite or there's an error
  if (isLoading) {
    return (
      <div className="bg-background-card rounded-xl border border-green-900/20 p-6">
        <div className="animate-pulse h-24 bg-background-elevated rounded"></div>
      </div>
    )
  }

  if (error || !data || !data.referralCode) {
    // User can't invite - don't show section
    return null
  }

  const copyReferralLink = async () => {
    try {
      await navigator.clipboard.writeText(data.referralLink)
      setCopiedCode(true)
      toast.success(t('referral:section.linkCopied'))
      setTimeout(() => setCopiedCode(false), 2000)
    } catch (e) {
      toast.error(t('common:errors.generic'))
    }
  }

  const validateWalletAddress = (address: string): boolean => {
    if (!address) {
      setWalletError(t('referral:section.walletRequired'))
      return false
    }
    if (!SOLANA_ADDRESS_REGEX.test(address)) {
      setWalletError(t('referral:section.walletInvalid'))
      return false
    }
    setWalletError('')
    return true
  }

  const handleSetWallet = () => {
    if (validateWalletAddress(walletInput)) {
      setWalletMutation.mutate(walletInput)
    }
  }

  const handleWalletInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWalletInput(e.target.value)
    if (walletError) {
      setWalletError('')
    }
  }

  return (
    <div className="bg-background-card rounded-xl border border-green-900/20 p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-gold-500/20 rounded-lg">
          <Network className="h-5 w-5 text-gold-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">{t('referral:section.title')}</h3>
          <p className="text-xs text-gray-400">{t('referral:section.subtitle')}</p>
        </div>
      </div>

      {/* Referral Code & Link */}
      <div className="bg-background-elevated rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-400">{t('referral:section.yourCode')}</span>
        </div>
        <div className="flex items-center gap-3">
          <code className="flex-1 text-lg font-mono text-gold-300 truncate">
            {data.referralCode}
          </code>
          <button
            onClick={copyReferralLink}
            className="flex items-center gap-2 px-3 py-2 bg-gold-500/20 text-gold-300 rounded-lg hover:bg-gold-500/30 transition-colors text-sm"
          >
            {copiedCode ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copiedCode ? t('referral:section.codeCopied') : t('referral:section.shareLink')}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2 truncate">
          {data.referralLink}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-background-elevated rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Users className="h-4 w-4 text-teal-400" />
            <span className="text-xs text-gray-400">{t('referral:section.activeReferrals')}</span>
          </div>
          <p className="text-xl font-bold text-white">{data.invitedUsersCount}</p>
        </div>
        <div className="bg-background-elevated rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="h-4 w-4 text-green-400" />
            <span className="text-xs text-gray-400">{t('referral:section.totalEarnings')}</span>
          </div>
          <p className="text-xl font-bold text-white">${Number(data.totalInvestmentsUSDT).toLocaleString()}</p>
        </div>
      </div>

      {/* Info Footer */}
      <div className="mt-4 pt-4 border-t border-green-900/20">
        <p className="text-xs text-gray-500 text-center mb-4">
          Share your link to earn commissions when your referrals make investments
        </p>

        {/* Referral Wallet Address Section */}
        <div className="bg-background-elevated rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Wallet className="h-4 w-4 text-gold-400" />
            <span className="text-sm font-medium text-white">Commission Wallet</span>
          </div>
          
          {user?.referralWalletAddress ? (
            // Show saved wallet address (read-only)
            <div>
              <p className="text-xs text-gray-400 mb-2">
                Your referral commissions will be sent to:
              </p>
              <code className="block text-sm font-mono text-green-400 bg-green-900/20 px-3 py-2 rounded-lg break-all">
                {user.referralWalletAddress}
              </code>
              <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                <Check className="h-3 w-3 text-green-400" />
                Wallet address is set and cannot be changed
              </p>
            </div>
          ) : (
            // Show input to set wallet address
            <div>
              <p className="text-xs text-gray-400 mb-2">
                {t('referral:section.walletPrompt')}
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={walletInput}
                  onChange={handleWalletInputChange}
                  placeholder={t('referral:section.walletPlaceholder')}
                  className={`flex-1 bg-background-primary border ${
                    walletError ? 'border-red-500' : 'border-gray-700'
                  } rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-gold-500`}
                  disabled={setWalletMutation.isPending}
                />
                <button
                  onClick={handleSetWallet}
                  disabled={setWalletMutation.isPending || !walletInput}
                  className="flex items-center gap-2 px-4 py-2 bg-gold-500 text-black rounded-lg hover:bg-gold-400 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {setWalletMutation.isPending ? (
                    <span className="animate-spin h-4 w-4 border-2 border-black border-t-transparent rounded-full" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Save
                </button>
              </div>
              {walletError && (
                <p className="text-xs text-red-400 mt-2 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {walletError}
                </p>
              )}
              <p className="text-xs text-yellow-500 mt-2 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                This can only be set once and cannot be changed later
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Referral Rewards Claim — show if has balance OR has active claim lock */}
      {((user?.availableReferralUSDT ?? 0) > 0 || (user?.availableReferralTAKARA ?? 0) > 0 ||
        user?.claimLockedUntil?.REFERRAL_USDT || user?.claimLockedUntil?.REFERRAL_TAKARA) && (
        <ReferralRewardsClaim
          user={user}
          publicKey={publicKey}
          claimReward={claimReward}
          queryClient={queryClient}
          isUsdtReferralModalOpen={isUsdtReferralModalOpen}
          setIsUsdtReferralModalOpen={setIsUsdtReferralModalOpen}
          isTakaraReferralModalOpen={isTakaraReferralModalOpen}
          setIsTakaraReferralModalOpen={setIsTakaraReferralModalOpen}
        />
      )}

      {/* Invited Users List */}
      {statsResponse && (
        <div className="mt-4 pt-4 border-t border-green-900/20">
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-teal-400" />
            <h4 className="text-sm font-medium text-white">{t('referral:section.invitedUsers')}</h4>
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {/* L1 Users */}
            {statsResponse.byLevel.L1.users.map((user) => (
              <div
                key={user.id}
                className="bg-background-elevated rounded-lg p-3 flex items-center justify-between"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-1.5 py-0.5 bg-teal-500/20 text-teal-300 rounded">L1</span>
                    <span className="text-sm font-mono text-white truncate">
                      {user.username || user.walletAddress?.slice(0, 8) + '...' || 'Anonymous'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Joined {new Date(user.joinedAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 text-sm font-semibold text-green-400">
                    <DollarSign className="h-3 w-3" />
                    <span>{user.totalInvestedUSDT.toLocaleString()} USDT</span>
                  </div>
                  {user.totalInvestedTAKARA > 0 && (
                    <div className="flex items-center gap-1 text-xs text-gold-300 mt-1">
                      <Wallet className="h-3 w-3" />
                      <span>{user.totalInvestedTAKARA.toLocaleString()} TAKARA</span>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* L2 Users */}
            {statsResponse.byLevel.L2.users.map((user) => (
              <div
                key={user.id}
                className="bg-background-elevated rounded-lg p-3 flex items-center justify-between"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded">L2</span>
                    <span className="text-sm font-mono text-white truncate">
                      {user.username || user.walletAddress?.slice(0, 8) + '...' || 'Anonymous'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Joined {new Date(user.joinedAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 text-sm font-semibold text-green-400">
                    <DollarSign className="h-3 w-3" />
                    <span>{user.totalInvestedUSDT.toLocaleString()} USDT</span>
                  </div>
                  {user.totalInvestedTAKARA > 0 && (
                    <div className="flex items-center gap-1 text-xs text-gold-300 mt-1">
                      <Wallet className="h-3 w-3" />
                      <span>{user.totalInvestedTAKARA.toLocaleString()} TAKARA</span>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* L3 Users */}
            {statsResponse.byLevel.L3.users.map((user) => (
              <div
                key={user.id}
                className="bg-background-elevated rounded-lg p-3 flex items-center justify-between"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-1.5 py-0.5 bg-purple-500/20 text-purple-300 rounded">L3</span>
                    <span className="text-sm font-mono text-white truncate">
                      {user.username || user.walletAddress?.slice(0, 8) + '...' || 'Anonymous'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Joined {new Date(user.joinedAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 text-sm font-semibold text-green-400">
                    <DollarSign className="h-3 w-3" />
                    <span>{user.totalInvestedUSDT.toLocaleString()} USDT</span>
                  </div>
                  {user.totalInvestedTAKARA > 0 && (
                    <div className="flex items-center gap-1 text-xs text-gold-300 mt-1">
                      <Wallet className="h-3 w-3" />
                      <span>{user.totalInvestedTAKARA.toLocaleString()} TAKARA</span>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* No Users Message */}
            {statsResponse.byLevel.L1.users.length === 0 &&
              statsResponse.byLevel.L2.users.length === 0 &&
              statsResponse.byLevel.L3.users.length === 0 && (
                <div className="text-center py-6 text-gray-500 text-sm">
                  No invited users yet. Share your referral link to start earning!
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  )
}
